interface PageRule {
  match_type: 'exact' | 'contains' | 'starts_with' | 'regex'
  value: string
}

interface TriggerSectionProps {
  idPrefix: string
  triggerType: 'event' | 'scheduled' | 'page' | 'all_pages'
  onTriggerTypeChange: (value: 'event' | 'scheduled' | 'page' | 'all_pages') => void
  triggerEvent: string
  onTriggerEventChange: (value: string) => void
  triggerPageRules: PageRule[]
  addPageRule: () => void
  updatePageRule: (index: number, field: keyof PageRule, value: string) => void
  removePageRule: (index: number) => void
  scheduledAt: string
  onScheduledAtChange: (value: string) => void
  delaySeconds: number
  onDelaySecondsChange: (value: number) => void
  delayHelpText?: string
  frequencyCap: string
  onFrequencyCapChange: (value: string) => void
  errors: Partial<Record<string, string>>
}

export default function TriggerSection({
  idPrefix,
  triggerType,
  onTriggerTypeChange,
  triggerEvent,
  onTriggerEventChange,
  triggerPageRules,
  addPageRule,
  updatePageRule,
  removePageRule,
  scheduledAt,
  onScheduledAtChange,
  delaySeconds,
  onDelaySecondsChange,
  delayHelpText = 'Aguardar estes segundos após o gatilho antes de mostrar a mensagem',
  frequencyCap,
  onFrequencyCapChange,
  errors,
}: TriggerSectionProps) {
  return (
    <div className="p-6 space-y-4 border-t border-gray-200">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Quando enviar
        </label>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="trigger_type"
              value="event"
              checked={triggerType === 'event'}
              onChange={() => onTriggerTypeChange('event')}
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Quando um evento é disparado</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="trigger_type"
              value="page"
              checked={triggerType === 'page'}
              onChange={() => onTriggerTypeChange('page')}
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Ao visitar uma página específica</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="trigger_type"
              value="all_pages"
              checked={triggerType === 'all_pages'}
              onChange={() => onTriggerTypeChange('all_pages')}
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Em todas as páginas</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="trigger_type"
              value="scheduled"
              checked={triggerType === 'scheduled'}
              onChange={() => onTriggerTypeChange('scheduled')}
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Em uma data/hora específica</span>
          </label>
        </div>
      </div>

      {triggerType === 'event' && (
        <div>
          <label
            htmlFor={`${idPrefix}_trigger_event`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Nome do Evento <span className="text-red-500">*</span>
          </label>
          <input
            id={`${idPrefix}_trigger_event`}
            type="text"
            value={triggerEvent}
            onChange={(e) => onTriggerEventChange(e.target.value)}
            placeholder="ex: cadastro_completo, compra_realizada"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
              errors.trigger_event ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
          />
          {errors.trigger_event && (
            <p className="mt-1 text-sm text-red-600">{errors.trigger_event}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Este evento deve ser rastreado via integração do widget
          </p>
        </div>
      )}

      {triggerType === 'page' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Regras de URL da Página
          </label>
          <div className="space-y-2">
            {triggerPageRules.map((rule, index) => (
              <div key={index} className="flex gap-2">
                <select
                  value={rule.match_type}
                  onChange={(e) => updatePageRule(index, 'match_type', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                >
                  <option value="exact">URL é exatamente</option>
                  <option value="contains">URL contém</option>
                  <option value="starts_with">URL começa com</option>
                  <option value="regex">URL corresponde a regex</option>
                </select>
                <input
                  type="text"
                  value={rule.value}
                  onChange={(e) => updatePageRule(index, 'value', e.target.value)}
                  placeholder="ex: /precos, /checkout"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
                <button
                  type="button"
                  onClick={() => removePageRule(index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  Remover
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPageRule}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              + Adicionar regra de URL
            </button>
          </div>
        </div>
      )}

      {triggerType === 'scheduled' && (
        <div>
          <label
            htmlFor={`${idPrefix}_scheduled_at`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Enviar em <span className="text-red-500">*</span>
          </label>
          <input
            id={`${idPrefix}_scheduled_at`}
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => onScheduledAtChange(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
              errors.scheduled_at ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
          />
          {errors.scheduled_at && (
            <p className="mt-1 text-sm text-red-600">{errors.scheduled_at}</p>
          )}
        </div>
      )}

      <div>
        <label
          htmlFor={`${idPrefix}_delay_seconds`}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Atraso após gatilho (segundos)
        </label>
        <input
          id={`${idPrefix}_delay_seconds`}
          type="number"
          min="0"
          value={delaySeconds}
          onChange={(e) => onDelaySecondsChange(parseInt(e.target.value) || 0)}
          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        />
        <p className="mt-1 text-xs text-gray-500">
          {delayHelpText}
        </p>
      </div>

      <div>
        <label
          htmlFor={`${idPrefix}_frequency_cap`}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Limite de frequência
        </label>
        <input
          id={`${idPrefix}_frequency_cap`}
          type="number"
          min="1"
          value={frequencyCap}
          onChange={(e) => onFrequencyCapChange(e.target.value)}
          placeholder="Sem limite"
          className={`w-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
            errors.frequency_cap ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
        />
        {errors.frequency_cap && (
          <p className="mt-1 text-sm text-red-600">{errors.frequency_cap}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Envia somente se o usuário tiver recebido menos que este número de mensagens
          automáticas (de qualquer mensagem do mesmo canal) nas últimas 24 horas. Deixe em
          branco para enviar sem limite.
        </p>
      </div>
    </div>
  )
}
